/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useEffect, useRef } from 'react'

import { useEbtContext } from '@modules/ebt/EbtContext'
import { EventRegistry } from '@modules/ebt/EventRegistry'

import type { SnakeCaseToPascalCase } from './types'

/**
 * Represents an event with its type and associated payload data.
 *
 * @template T - The type of the event identifier, defaults to string, but most commonly a union of strings
 * @property {T} eventType - The identifier/type of the event
 * @property {Record<string, any>} payload - The payload associated with the event
 */
export type EventTypeAndPayload<T extends string = string> = {
  eventType: T
  payload: Record<string, string | number | boolean | unknown>
}

/**
 * Utility type to get the payload type corresponding to a specific event type discriminated by a property value.
 *
 * @template T - The discriminating event type
 * @template U - The union of possible event type and payloads
 * @property {T} eventType - The identifier/type of the event
 */
type TypedEventPayload<T extends string, U extends EventTypeAndPayload> = (U & {
  eventType: T
})['payload']

/**
 * Utility type that creates event reporting methods for EBT (Event-Based Telemetry).
 *
 * This type generates a map of functions with the naming pattern `report{EventName}Ebt`,
 * where each function handles reporting a specific type of event with its corresponding payload.
 *
 * @template T - String literal type representing event names in snake_case
 * @template U - Event type and payload definition that extends EventTypeAndPayload
 *
 * @example
 * // If T includes 'user_click' and U defines the payload for 'user_click'
 * // The resulting type will include a method: reportUserClickEbt(arg: TypedEventPayload<'user_click', U>): void
 *
 * @returns An object type with methods for reporting different types of events
 */
export type EbtHookReturnType<T extends string, U extends EventTypeAndPayload> = {
  [K in T as `report${SnakeCaseToPascalCase<K>}Ebt`]: (arg: TypedEventPayload<K, U>) => void
}

/**
 * This type helps forcing the event type and payload structure.
 */
type EventTypeSchema<T extends string, U extends EventTypeAndPayload> = T extends string
  ? {
      eventType: T
      schemaProps: {
        [_ in keyof TypedEventPayload<T, U>]: ConstructorParameters<
          typeof EventRegistry<T, U>
        >[0]['schemaProps'][keyof U]
      }
    }
  : never

/**
 * A reusable React hook that manages event-based tracking (EBT) functionality.
 * This is meant to serve both as an abstraction over the EBT classes and the related boilerplate as well as a
 * type-safe standard for event reporting.
 *
 * @template TEventTypeAndPayload - The union of event types and payloads that can be reported
 *
 * @param eventRegistryArgs - One or more event registry configurations, each containing an event type and its
 * corresponding schema properties
 *
 * @returns A function that takes an event type and its corresponding payload and reports the event
 * @returns {(type: TEventTypeAndPayload['eventType'], args: TEventTypeAndPayload['payload']) => void}
 *
 * @example
 * ```typescript
 * const reportEbtEvent = useEbt(
 *   {
 *     eventType: 'my_event',
 *     schemaProps: { someArg: { description: 'Some argument' } },
 *   },
 *   {
 *     eventType: 'another_event',
 *     schemaProps: { anotherArg: { description: 'Another argument' } },
 *   }
 * );
 *
 * reportEbtEvent('my_event', { someArg: 'value' });
 * reportEbtEvent('another_event', { anotherArg: 'any another value' });
 * ```
 *
 * @note This hook requires the EbtContext provider to be present in the component tree.
 */
const useEbt = <TEventTypeAndPayload extends EventTypeAndPayload>(
  ...eventRegistryArgs: ReadonlyArray<
    EventTypeSchema<TEventTypeAndPayload['eventType'], TEventTypeAndPayload>
  >
): (<T extends TEventTypeAndPayload['eventType']>(
  type: T,
  args: Extract<TEventTypeAndPayload, { eventType: T }>['payload'],
) => void) => {
  const { analytics } = useEbtContext()
  const hasBeenRegister = useRef(false)

  useEffect(() => {
    if (analytics && !hasBeenRegister.current) {
      hasBeenRegister.current = true

      try {
        eventRegistryArgs.forEach((item) => {
          const registry = new EventRegistry({
            ...item,
            analytics,
          })

          registry.registerEvent()
        })
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(e)
        }
      }
    }
  }, [analytics, eventRegistryArgs])

  const reportEbtEvent = <T extends TEventTypeAndPayload['eventType']>(
    type: T,
    args: Extract<TEventTypeAndPayload, { eventType: T }>['payload'],
  ) => {
    try {
      if (analytics) {
        analytics.reportEvent(type, args)
      }
    } catch (e) {
      console.error(e)
    }
  }

  return reportEbtEvent
}

export default useEbt
